import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import * as moment from 'moment';

import {DropDownMenu, Leaf} from './tree-diagram.interfaces';

import * as go from 'gojs';
import {InputEvent, Point} from 'gojs';
import {Employee} from '../../+employees/emp-tree/emp-tree.interfaces';
import {OrgEntity} from '../../+organizations/org-interfaces';
import {OrgTreeService} from '../../+organizations/org-tree.service';
import {
  DATE_MAX,
  GP_AVATAR_BG,
  GP_AVATAR_BORDER,
  GP_AVATAR_INITIALS,
  GP_NODE_BG,
  GP_NODE_BG_DISABLED,
  GP_NODE_BORDER,
  GP_NODE_BORDER_ERROR,
  GP_NODE_NAME_TEXT,
  GP_NODE_SUBTITLE_TEXT,
  GP_NODE_TEXT
} from '../../app.constants';
import {DateLocalizationPipe} from '../../shared/pipes/date-localization.pipe';
import {ApiService} from '../api';
import {ModalService} from '../modal';
import {AbbreviationPipe} from '../pipes/abbreviation.pipe';
import {Files} from '../utils/files';
import {isPresent} from '../utils/lang';

(go as any).Diagram.licenseKey = `238647e1b3634cc702d90776423d68bc5cf07e34ce960ef6580010f4e55b6840259aef7801dbdec4d2f14bf8187b97dd8dc26f2b9e1c5129f736838c4be383aee76373b111081787f40621cacefe7ea7ab7a74f492b77ff28f7c8aa6eaaac69f0ee0a18118cf0da9397e5361507efd1ab6e9c97bfe04cb4f63`;

export interface LoadNodeChildrenEvent {
  nodeId: number;
  callback: Function;
}

@Component({
  selector: 'gp-tree-diagram',
  styleUrls: ['./tree-diagram.component.scss'],
  templateUrl: './tree-diagram.component.html'
})

export class TreeDiagramComponent implements OnChanges, OnInit {

  public diagram: any;
  public dropdownMenu: any = {
    top: 0,
    left: 0,
    opened: false
  };
  @Input() public diagramHeight: string;
  @Input() public dropDownMenu: DropDownMenu[];
  @Input() public selectedLeaf: Employee;
  @Input() public selectedTree;
  @Input() public leafColorCallback;
  @Input() public fieldName: string = 'name';
  @Input() public fieldBackupName: string = 'backupName';
  @Input() public fieldBackupSubTitle1: string = 'subTitle1';
  @Input() public fieldBackupSubTitle2: string = 'subTitle2';
  @Input() public fieldBackupSubTitle3: string = 'subTitle3';
  @Input() public defaultDate: string;
  @Input() public isReadOnly: boolean;
  @Input() public isFocus: boolean;
  @Input() public isDated: boolean = true;

  @Output() public createChild: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onEditOrg: EventEmitter<any> = new EventEmitter<any>();
  @Output() public onDeleteOrg: EventEmitter<any> = new EventEmitter<any>();
  @Output() public orgParentChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() public showManageForm: EventEmitter<void> = new EventEmitter<void>();

  @Output() public leafChanged: EventEmitter<any> = new EventEmitter<any>();

  @Output() public setRootNode: EventEmitter<number> = new EventEmitter<number>();
  @Output() public loadNodeChildren: EventEmitter<LoadNodeChildrenEvent> = new EventEmitter<LoadNodeChildrenEvent>();

  @Output() public dateChanged: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('chartContainer', {static: true}) public chartContainer: ElementRef;

  @ViewChild('dropdown', {static: true}) public dropdown: ElementRef;
  public date: string;
  private maxLevel: number = 0;

  constructor(private modalService: ModalService,
              private translate: TranslateService,
              private dateLocalizationPipe: DateLocalizationPipe ,
              private orgTreeService: OrgTreeService,
              private apiService: ApiService) {
  }

  public ngOnInit(): void {
    this.date = this.defaultDate;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedTree && isPresent(this.selectedTree) && isPresent(this.selectedTree.children)) {
      if (!this.diagram) {
        this.diagram = this.createDiagram();
        this.diagram.scale = 0.75;
      }
      this.selectedTree.children.forEach(item => {
        if (item.parent === null) {
          delete item.parent;
        }
      });
      this.setReadOnly(this.isReadOnly);
      this.diagram.model.nodeDataArray = [...this.selectedTree.children];
    }
    this.diagram.updateAllTargetBindings();

    // Center the selected node when the selection changes (e.g. when clicking a node),
    // matching the org-tree behaviour. The dropdown guard inside scrollToOrgNode prevents
    // re-centering when the 3-dot context menu is opened.
    if (changes.selectedLeaf) {
      setTimeout(() => this.scrollToOrgNode());
    }
  }

  public setReadOnly(isReadOnly: boolean) {
    this.diagram.startTransaction('dots visible');
    this.diagram.model.setDataProperty(this.diagram.model.modelData, 'isDotsVisible', !isReadOnly);
    this.diagram.commitTransaction('dots visible');
  }

  public onChangeDate(date: string) {
    if (date) {
      this.dateChanged.emit(date);
    }
  }

  public takeScreenshot() {
    const diagramHeight: number = this.diagram.documentBounds.height;
    const diagramWidth: number = this.diagram.documentBounds.width;
    const base64Code: string = this.diagram.makeImageData({
      size: new go.Size(diagramWidth, diagramHeight),
      maxSize: new go.Size(diagramWidth, diagramHeight)
    });

    if (!base64Code.split(',')[1]) {
      this.modalService.alert({
        title: this.translate.instant('COMMON.alert'),
        text: this.translate.instant('FORMS.ERRORS.too_big_image')
      });

      return;
    }

    const decodedData = atob(base64Code.split(',')[1]);
    const charArray = [];
    for (let i = 0; i < decodedData.length; i++) {
      charArray.push(decodedData.charCodeAt(i));
    }
    const blob = new Blob([new Uint8Array(charArray)], {type: 'image/png'});
    Files.downloadBlob(blob);
  }

  public focusOnOrgNode(leaf?: Leaf, zoom: boolean = true) {
    const selectedLeaf = leaf || this.selectedLeaf;
    if (isPresent(selectedLeaf) && this.diagram) {
      const node: go.Node = this.diagram.findNodeForKey(selectedLeaf.id);
      if (node) {
        if (zoom) {
          this.diagram.zoomToRect(node.actualBounds, 0.75);
        }
        this.diagram.centerRect(node.actualBounds);
      }
    }
  }

  public availableLevels() {
    // const org = this.getSelectedOrg();
    //
    // if (!org) {
    //   return [];
    // }

    return [1];
    // return this.orgTreeService.availableLevels(this.selectedTree.entityTypes, org.entityLevel);
  }

  public manageOrg(): void {
    const org = this.getSelectedOrg();

    if (!org) {
      return;
    }

    this.showManageForm.emit(org.repEntity.id);
  }

  public editEntity() {
    const org = this.getSelectedOrg();

    if (!org) {
      return;
    }

    const prevParent: number = org.parent;

    this.onEditOrg.next({
      attributes: org,
      callback: (data) => {
        this.diagram.startTransaction('Edit company');
        // Object.assign(org, data);
        this.diagram.updateAllTargetBindings();
        this.diagram.updateAllRelationshipsFromData();
        this.diagram.commitTransaction('Edit company');
      }
    });
  }

  public addCopyEntity() {
    const org = this.getSelectedOrg();

    if (!org) {
      return;
    }

    const data = Object.assign({}, org, {hasChildren: false});

    this.onEditOrg.next({
      attributes: data,
      isCopy: true,
      callback: (newEntity: OrgEntity) => {
        this.loadNodeChildren.next({
          nodeId: org.id,
          callback: (nodes) => {
            const node = this.diagram.findNodeForKey(org.id);
            node.diagram.commandHandler.expandTree(node);
            this.diagram.startTransaction('add company');
            this.diagram.model.addNodeDataCollection(nodes);
            this.diagram.commitTransaction('add company');
          }
        });
        // this.diagram.startTransaction('copy company');
        // this.diagram.model.addNodeData(newEntity);
        // this.diagram.commitTransaction('copy company');
      }
    });
  }

  public addEntity() {
    const org = this.getSelectedOrg();

    if (!org) {
      return;
    }

    this.createChild.next({
      parentOrgId: org.id,
      callback: (newEntity: OrgEntity) => {
        this.loadNodeChildren.next({
          nodeId: org.id,
          callback: (nodes) => {
            const node = this.diagram.findNodeForKey(org.id);
            node.diagram.commandHandler.expandTree(node);
            this.diagram.startTransaction('add company');
            this.diagram.model.addNodeDataCollection(nodes);
            this.diagram.commitTransaction('add company');
          }
        });
        // this.diagram.startTransaction('add company');
        // this.diagram.model.addNodeData(newEntity);
        // this.diagram.commitTransaction('add company');
      }
    });
  }

  public deleteEntity(): void {
    const org = this.getSelectedOrg();

    if (!org) {
      return;
    }

    this.onDeleteOrg.next({
      id: org.repEntity.id,
      callback: () => {
        this.diagram.startTransaction('remove company');
        this.diagram.model.removeNodeData(org);
        this.diagram.commitTransaction('remove company');
      }
    });
  }

  public openDebugFlatten() {
    const entity = this.getSelectedOrg();
    if (!entity) {
      return;
    }
    this.orgTreeService.openDebugFlatten(entity.repEntity.id);
  }

  private setMaxLevel(): void {
    this.maxLevel = 0;
    this.selectedTree.entityTypes.forEach(type => {
      if (type.level && type.level > this.maxLevel) {
        this.maxLevel = type.level;
      }
    });

    this.maxLevel = this.maxLevel * 2;
  }

  private createDiagram(): any {
    const graphObject: any = go.GraphObject.make;
    const diagram: any = this.getDiagramConfig(graphObject);
    // this is used to determine feedback during drags
    this.defineBuilder();
    diagram.nodeTemplate = this.getNodeConfig(graphObject);
    diagram.model = go.Model.fromJson({
      'class': 'go.TreeModel',
      nodeParentKeyProperty: 'parent',
      nodeKeyProperty: 'id'
      // nodeDataArray: []
    });

    let init: boolean = false;
    diagram.addDiagramListener('InitialLayoutCompleted', (e) => {
      init = true;
    });

    diagram.addDiagramListener('AnimationFinished', (e) => {
      if (init) {
        this.focusOnOrgNode(this.selectedLeaf, false);
        init = false;
      }
    });

    return diagram;
  }

  private getDiagramConfig($) {
    let isDragged: boolean = false;
    const viewPoint: Point = new go.Point();
    const dragStep: number = 25;
    const diagramClientRect: ClientRect = this.chartContainer.nativeElement.getBoundingClientRect();

    const drag = (fn: () => boolean) => {
      setTimeout(() => {
        if (isDragged && fn()) {
          drag(fn);
        }
      }, 100);
    };

    const dragLeft = () => {
      if (viewPoint.x < 0) {
        diagram.position = new go.Point(diagram.position.x - dragStep, diagram.position.y);
        return true;
      }
    };

    const dragTop = () => {
      if (viewPoint.y < 0) {
        diagram.position = new go.Point(diagram.position.x, diagram.position.y - dragStep);
        return true;
      }
    };

    const dragBottom = () => {
      if (viewPoint.y > diagramClientRect.height) {
        diagram.position = new go.Point(diagram.position.x, diagram.position.y + dragStep);
        return true;
      }
    };

    const dragRight = () => {
      if (viewPoint.x > diagramClientRect.width) {
        diagram.position = new go.Point(diagram.position.x + dragStep, diagram.position.y);
        return true;
      }
    };

    const diagram = $(go.Diagram, this.chartContainer.nativeElement, {
      autoScrollRegion: new go.Margin(100, 100, 100, 100),
      scrollHorizontalLineChange: 100,
      scrollVerticalLineChange: 50,
      initialContentAlignment: go.Spot.Center,
      validCycle: go.Diagram.CycleDestinationTree, // make sure users can only create trees
      maxSelectionCount: 1, // users can select only one part at a time
      mouseDragOver: (e: InputEvent) => {
        isDragged = true;
        viewPoint.set(e.viewPoint);
        drag(dragLeft);
        drag(dragRight);
        drag(dragTop);
        drag(dragBottom);
      },
      mouseDrop: () => {
        isDragged = false;
      },
      scrollMode: go.Diagram.DocumentScroll,
      layout: $(go.TreeLayout, {
        treeStyle: go.TreeLayout.StyleLastParents,
        arrangement: go.TreeLayout.ArrangementHorizontal,
        // properties for most of the tree:
        angle: 90,
        layerSpacing: 35,
        // properties for the 'last parents':
        alternateAngle: 90,
        alternateLayerSpacing: 35,
        alternateAlignment: go.TreeLayout.AlignmentBus,
        alternateNodeSpacing: 20,
        commitNodes: () => {
          go.TreeLayout.prototype['commitNodes'].call(diagram.layout);  // do the standard behavior
          // then go through all of the vertexes and set their corresponding node's Shape.fill
          // to a brush dependent on the TreeVertex.level value
          diagram.layout.network.vertexes.each((v: any) => {
            if (v.node) {
              const shape: any = v.node.findObject('SHAPE');
              if (shape) {
                shape.fill = $(go.Brush, 'Linear', {
                  // 0: '#6986FB',
                  0: this.getNodeColor(v.node.data),
                  start: go.Spot.Left,
                  end: go.Spot.Right
                });
              }
            }
          });
        }
      }),
      'LayoutCompleted': (e) => {
        if (this.isFocus) {
          setTimeout(() => this.focusOnOrgNode(), 1000);
        }
      },
      'undoManager.isEnabled': false, // enable undo & redo
      linkTemplate: $(go.Link, go.Link.Orthogonal,
        {corner: 5, relinkableFrom: false, relinkableTo: false},
        $(go.Shape, {strokeWidth: 1, stroke: 'silver'})) // the link shape
    });
    return diagram;
  }

  private defineBuilder() {
    go.GraphObject.defineBuilder('GPTreeExpanderButton', (args) => {
      const button = /** @type {Panel} */ (
        go.GraphObject.make('Button',
          { // set these values for the isTreeExpanded binding conversion
            '_treeExpandedFigure': 'MinusLine',
            '_treeCollapsedFigure': 'PlusLine'
          },
          go.GraphObject.make(go.Shape,  // the icon
            {
              name: 'ButtonIcon',
              figure: 'MinusLine',  // default value for isTreeExpanded is true
              desiredSize: new go.Size(6, 6)
            },
            // bind the Shape.figure to the Node.isTreeExpanded value using this converter:
            new go.Binding('figure', '', (node, shape) => {
              return (node.isTreeExpanded && node.findTreeChildrenNodes().count)
                ? shape.panel['_treeExpandedFigure']
                : shape.panel['_treeCollapsedFigure'];
            })
              .ofObject()),
          // assume initially not visible because there are no links coming out
          {visible: false},
          new go.Binding('visible', 'hasChildren', (hasChildren) => hasChildren)
        )
      );

      // tree expand/collapse behavior
      button.click = (e, button) => {
        let node: go.Node = button.part as go.Node;
        if (node.findTreeChildrenNodes().count) {
          if (node instanceof go.Adornment) {
            node = node.adornedPart as go.Node;
          }
          if (!(node instanceof go.Node)) {
            return;
          }
          const diagram = node.diagram;
          if (diagram === null) {
            return;
          }
          const cmd = diagram.commandHandler;
          if ((node.isTreeExpanded && !cmd.canCollapseTree(node)) ||
            (!node.isTreeExpanded && !cmd.canExpandTree(node))) {
            return;
          }
          e.handled = true;
          if (node.isTreeExpanded) {
            cmd.collapseTree(node);
          } else {
            cmd.expandTree(node);
          }
        } else {
          this.loadNodeChildren.next({
            nodeId: node.data.id,
            callback: (nodes) => {
              node.diagram.commandHandler.expandTree(node);
              this.diagram.startTransaction('add company');
              this.diagram.model.addNodeDataCollection(nodes);
              this.diagram.commitTransaction('add company');
            }
          });
        }
      };

      return button;
    });
  }

  private getNodeConfig($) {
    const textStyle: () => any = () => {
      return {font: '9pt  Segoe UI,sans-serif', stroke: GP_NODE_TEXT};
    };

    const textStyleSmall: () => any = () => {
      return {font: '10pt  Segoe UI,sans-serif', stroke: GP_NODE_TEXT};
    };

    const littleCircle: () => any = () => $(go.Shape, 'Circle', {
      width: 3,
      height: 3,
      fill: 'black',
      stroke: null,
      margin: new go.Margin(1, 10, 0, 20),
      cursor: 'pointer'
    });

    return $(go.Node, 'Vertical', {
        selectionObjectName: 'BODY', deletable: false,
        mouseDragLeave: (e: any, node: any) => {
          if (this.selectedTree.name === 'HierarchicalOrganization') {
            return;
          }
          const shape: any = node.findObject('SHAPE');
          if (shape && shape._prevFill) {
            shape.fill = shape._prevFill;  // restore the original brush
          }
        },
        mouseDrop: (e: any, node: any) => {
          if (this.selectedTree.name === 'HierarchicalOrganization') {
            return;
          }
          const diagramOfNode: any = node.diagram;
          const selnode: any = diagramOfNode.selection.first();  // assume just one Node in selection
          if (this.mayWorkFor(selnode, node)) {
            this.changeOrgParent(selnode.data.repEntity, node.data.repEntity.id).subscribe(() => {
              this.commitChangeParent(selnode, node);
            });
          }
        },
        doubleClick: (e: any, node: any) => {
          this.setRootNode.next(node.data.id);
        },
        selectionAdorned: false,
        selectionChanged: (element) => {
          this.selectedLeaf = element.data;
          this.diagram.updateAllTargetBindings();
          this.leafChanged.emit(element.data);
        }
      },
      $(go.Panel, 'Auto',
        {name: 'BODY'},
        $(go.Shape, 'RoundedRectangle',
          {
            name: 'SHAPE', fill: 'white', stroke: null, strokeWidth: 5,
            fromLinkable: false, toLinkable: false, cursor: 'pointer',
            margin: new go.Margin(0, 0, 0, 0)
          },
          new go.Binding('stroke', '', (v: Leaf) => {
            if (v.position && !v.positions.length) {
              return GP_NODE_BORDER_ERROR;
            }

            return GP_NODE_BORDER;
          }),
          new go.Binding('strokeWidth', '', (v: Leaf) => {
            if (this.selectedLeaf && v.id === this.selectedLeaf.id) {
              return 5;
            }

            if (v.position && !v.positions.length) {
              return 2;
            }

            return 2;
          })
        ),
        $(go.Panel, go.Panel.Position,
          $(go.Panel, 'Horizontal',
            {defaultAlignment: go.Spot.Top},
            // Fade the original position node back when an active backup card sits on top of it
            new go.Binding('opacity', 'isBackupActive', (active: boolean) => (active ? 0.35 : 1)),
            $(go.Panel, 'Spot',
              {isClipping: true, alignment: go.Spot.TopLeft, margin: new go.Margin(10, 5, 5, 10)},
              $(go.Shape, 'Circle', {
                  width: 60,
                  height: 60,
                  margin: new go.Margin(5, 5, 5, 10)
                }
              ),
              $(go.Shape, 'Circle', {
                  strokeWidth: 1.5,
                  stroke: GP_AVATAR_BORDER,
                  width: 60,
                  height: 60,
                  margin: new go.Margin(5, 5, 5, 10),
                  fill: GP_AVATAR_BG
                }
              ),
              $(go.TextBlock, {
                  width: 45,
                  font: 'bold 20px Calibri',
                  textAlign: 'center',
                  stroke: GP_AVATAR_INITIALS
                },
                new go.Binding('text', 'name', (value: string) => {
                  return new AbbreviationPipe().transform(value);
                })
              ),
              $(go.Picture,
                {
                  width: 70,
                  height: 70
                },
                new go.Binding('source', '', (val: any) => {
                  return val.isPhotoBase64
                    ? 'data:image/png;base64,' + val.photoHref
                    : this.apiService.apiHost + val.photoHref;
                })
              )
            ),
            // define the panel where the text will appear
            $(go.Panel, 'Table',
              {
                maxSize: new go.Size(160, 999),
                margin: new go.Margin(6, 10, 0, 10),
                defaultAlignment: go.Spot.Left
              },
              $(go.RowColumnDefinition, {column: 2, width: 2}),
              new go.Binding('itemArray', 'subtitles'),
              {
                itemTemplate:
                  $(go.Panel, 'TableRow',
                    $(go.TextBlock, textStyleSmall(),
                      {margin: new go.Margin(2, 0, 10, 0), maxSize: new go.Size(160, 999)},
                      new go.Binding('text', 'name'),
                      new go.Binding('stroke', '', (a) => {
                        // name (first row / position) is bold black, all other subtitles are soft grey
                        return (a.isPosition || a.id === 0) ? GP_NODE_NAME_TEXT : GP_NODE_SUBTITLE_TEXT;
                      }),
                      new go.Binding('font', '', (a) => {
                        if (a.font) {
                          return a.font;
                        }

                        if (a.isPosition || a.id === 0) {
                          return 'bold 12px Segoe UI,sans-serif';
                        }

                        return '12px Segoe UI,sans-serif';
                      })
                    )
                  )
              }
            ),
            $(go.Panel, 'Spot', {
                alignment: go.Spot.Top,
                defaultAlignment: go.Spot.MiddleLeft,
                visible: false,
                click: (e) => {
                  this.dropdownMenu.left = e.viewPoint.x + 'px';
                  this.dropdownMenu.top = e.viewPoint.y + 'px';
                  setTimeout(() => this.dropdownMenu.opened = true);
                  setTimeout(() => {
                    const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
                    const windowHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
                    const dropdownRect = this.dropdown.nativeElement.getBoundingClientRect();
                    if (windowWidth < dropdownRect.right) {
                      this.dropdownMenu.left = (e.viewPoint.x - (dropdownRect.right - windowWidth + 10)) + 'px';
                    }
                    // Shift the menu up when it would overflow the bottom of the viewport so all
                    // actions stay visible (the menu is also scrollable via CSS as a fallback).
                    if (windowHeight < dropdownRect.bottom) {
                      this.dropdownMenu.top = (e.viewPoint.y - (dropdownRect.bottom - windowHeight + 10)) + 'px';
                    }
                  });
                }
              },
              new go.Binding('visible', 'isDotsVisible').ofModel(),
              new go.Binding('visible', '', (data: any) => {
                  return (data.availableForChange || data.position) && !this.isReadOnly;
                }
              ),
              $(go.Shape, {width: 10, height: 30, fill: 'transparent', stroke: 'transparent', cursor: 'pointer'}),
              $(go.Panel, 'Vertical', littleCircle(), littleCircle(), littleCircle())
            )
          ),
          $(go.Panel, 'Auto',
            {
              position: new go.Point(30, 30),
              visible: false
            },
            new go.Binding('visible', 'isBackupActive'),
            // White card with soft-blue border — same style as the current node
            $(go.Shape, 'RoundedRectangle',
              {fill: GP_NODE_BG, stroke: GP_NODE_BORDER, strokeWidth: 2}
            ),
            $(go.Panel, 'Horizontal',
              {defaultAlignment: go.Spot.Top, margin: new go.Margin(4, 6, 4, 4)},
              $(go.Panel, 'Spot',
                {isClipping: true, margin: new go.Margin(5, 5, 5, 5)},
                $(go.Shape, 'Circle', {
                    width: 50,
                    height: 50
                  }
                ),
                $(go.Shape, 'Circle', {
                    strokeWidth: 1.5,
                    stroke: GP_AVATAR_BORDER,
                    width: 50,
                    height: 50,
                    fill: GP_AVATAR_BG
                  }
                ),
                $(go.TextBlock, {
                    width: 45,
                    font: 'bold 18px Calibri',
                    textAlign: 'center',
                    stroke: GP_AVATAR_INITIALS
                  },
                  new go.Binding('text', this.fieldBackupName, (value: string) => {
                    return new AbbreviationPipe().transform(value);
                  })
                ),
                $(go.Picture,
                  {
                    width: 50,
                    height: 50
                  },
                  new go.Binding('source', 'backupPhotoHref', (val: any) => {
                    return this.apiService.apiHost + val;
                  })
                )
              ),
              // define the panel where the text will appear
              $(go.Panel, 'Table',
                {
                  maxSize: new go.Size(160, 999),
                  margin: new go.Margin(6, 10, 0, 3),
                  defaultAlignment: go.Spot.Left
                },
                $(go.RowColumnDefinition, {column: 2, width: 2}),
                $(go.TextBlock, textStyle(),
                  {
                    editable: false,
                    row: 0,
                    column: 0,
                    columnSpan: 5,
                    stroke: GP_NODE_NAME_TEXT,
                    font: 'bold 12px Segoe UI,sans-serif',
                    isMultiline: false
                  },
                  new go.Binding('text', this.fieldBackupName).makeTwoWay()),
                $(go.TextBlock, textStyleSmall(),
                  {row: 1, column: 0, columnSpan: 5, stroke: GP_NODE_SUBTITLE_TEXT, isMultiline: false, margin: new go.Margin(20, 0, 2, 0)},
                  new go.Binding('text', 'backupBeginDate', (date: string) => {
                    return date ? this.dateLocalizationPipe.transform(date) : '';
                  })
                ),
                $(go.TextBlock, textStyleSmall(),
                  {row: 2, column: 0, columnSpan: 5, stroke: GP_NODE_SUBTITLE_TEXT, isMultiline: false, margin: new go.Margin(2, 0, 2, 0)},
                  new go.Binding('text', 'backupEndDate', (date: string) => {
                    return date ? this.dateLocalizationPipe.transform(date) : '';
                  })
                ),
                $(go.TextBlock, textStyleSmall(),
                  {row: 3, column: 0, columnSpan: 5, stroke: GP_NODE_SUBTITLE_TEXT, isMultiline: false, margin: new go.Margin(5, 0, 2, 0)},
                  new go.Binding('text', this.fieldBackupSubTitle1)
                ),
                $(go.TextBlock, textStyleSmall(),
                  {row: 4, column: 0, columnSpan: 5, stroke: GP_NODE_SUBTITLE_TEXT, isMultiline: false, margin: new go.Margin(2, 0, 2, 0)},
                  new go.Binding('text', this.fieldBackupSubTitle2)
                ),
              )
            )
          )
        )
      ),
      $('GPTreeExpanderButton',
        {alignment: go.Spot.Bottom, alignmentFocus: go.Spot.Top}
      )
    );
  }

  private scrollToOrgNode() {
    // Don't re-center the node while the 3-dot context menu is open: opening the menu selects the
    // node (which changes selectedLeaf -> ngOnChanges -> this method), and centering would move the
    // node away from the freshly-positioned menu. Matches the org-tree behaviour.
    if (this.dropdownMenu.opened) {
      return;
    }
    if (isPresent(this.selectedLeaf) && this.diagram) {
      const node: go.Node = this.diagram.findNodeForKey(this.selectedLeaf.id);
      if (node) {
        this.diagram.centerRect(node.actualBounds);
      }
    }
  }

  private getNodeColor(v: any) {
    return v.isBackupActive === true || v.isDisabled ?
      GP_NODE_BG_DISABLED :
      GP_NODE_BG;
  }

  private getSelectedOrg() {
    const selNode = this.getSelectedNode();

    if (!selNode && this.selectedLeaf) {
      return this.selectedLeaf;
    }

    if (!selNode) {
      return;
    }

    return selNode.data;
  }

  private getSelectedNode() {
    if (!this.diagram) {
      return;
    }

    const selNode = this.diagram.selection.first();

    if (!(selNode instanceof go.Node)) {
      return;
    }

    return selNode;
  }

  private changeOrgParent(org, parentOrgId: number) {
    const newOrg: any = {
      allowclireg: org.allowclireg,
      begindate: org.begindate,
      cliReg: org.cliReg,
      enddate: org.enddate,
      entityLevel: org.entityLevel,
      entityType: org.entityType,
      name: org.name,
      organization: org.organization,
      parent: parentOrgId,
      parentBeginDate: moment().format('YYYY-MM-DD'),
      parentEndDate: DATE_MAX,
      searchFlagColour: org.searchFlagColour,
      subTitle1: org.subTitle1,
      subTitle2: org.subTitle2,
      subTitle3: org.subTitle3,
      tree: org.tree,
      treeType: this.selectedTree.type,
      userSelectable: org.userSelectable,
      type: org.type
    };
    return this.orgTreeService.updateOrgEntity(org.id, newOrg);
  }

  private commitChangeParent(node: any, parent: any): void {
    if (this.mayWorkFor(node, parent)) {
      // find any existing link into the selected node
      const link = node.findTreeParentLink();
      this.diagram.startTransaction('change parent');
      if (link !== null) {  // reconnect any existing link
        link.fromNode = parent;
      } else {  // else create a new link
        this.diagram.toolManager.linkingTool.insertLink(parent, parent.port, node, node.port);
      }
      this.diagram.commitTransaction('change parent');
      this.selectedLeaf.parent = parent.data.id;
      this.orgParentChanged.next();
      this.loadNodeChildren.next({
        nodeId: parent.data.id,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        callback: () => {
        }
      });
    }
  }

  private mayWorkFor(node1: any, node2: any) {
    return !this.selectedTree.supportLevel
      && node1 instanceof go.Node
      && node2 instanceof go.Node
      && node1 !== node2
      && !node2.isInTreeOf(node1)
      && node1.findTreeParentNode() !== node2;
  }
}
